import { useCallback } from "react";
import calendarModes from "../../utils/constants/calendarModes";
import calendarUserTypes from "../../utils/constants/calendarUserTypes";
import useCalendarFiltersByDashboard from "../../hooks/useCalendarFiltersByDashboard";
import activitiesStatus from "../../utils/constants/activities/activitiesStatus";

const typeFieldMapper = {
  TASK: "taskSelected",
  APPOINTMENT: "appointmentSelected",
  DEADLINE: "deadlineSelected",
  HEARING: "hearingSelected"
};

const statusMapper = {
  DONE: activitiesStatus.DONE,
  IN_PROGRESS: activitiesStatus.IN_PROGRESS,
  LATE: activitiesStatus.OVERDUE
};

const typesDefaultFilter = {
  appointmentSelected: true,
  taskSelected: true,
  deadlineSelected: true,
  hearingSelected: true
};

export default function useCalendarFilter() {
  const { goToCalendarWithDashboardFilters } = useCalendarFiltersByDashboard();

  const getTypeFilter = useCallback(
    (type: string | number) =>
      type
        ? {
          appointmentSelected: false,
          taskSelected: false,
          deadlineSelected: false,
          hearingSelected: false,
          [typeFieldMapper[type as keyof typeof typeFieldMapper]]: true
        }
        : typesDefaultFilter,
    []
  );

  const getResponsibleFilter = useCallback(
    (userId: { toString: () => any; }) => ({
      userFilter: {
        selected: userId ? [userId.toString()] : [],
        type: calendarUserTypes.RESPONSIBLE
      },
      allResponsible: !userId
    }),
    []
  );

  const getInfoToCache = useCallback(
    ({ status, type, userId, period = {} }: { status: string, type: string, userId: number, period: { fromDate?: moment.Moment, toDate?: moment.Moment } }) => ({
      lastView: calendarModes.LIST,
      queryDTO: {
        tags: [],
        status,
        start: period.fromDate,
        end: period.toDate,
        ...getTypeFilter(type),
        ...getResponsibleFilter(userId)
      }
    }),
    [getResponsibleFilter, getTypeFilter]
  );

  const CALENDAR_ROUTE = "#/main/calendar";

  const goWithFilter = useCallback(
    (params: { status: keyof typeof statusMapper, type: string, userId: number, period: { fromDate?: moment.Moment, toDate?: moment.Moment } }) => {
      return goToCalendarWithDashboardFilters(
        getInfoToCache({ ...params, status: statusMapper[params.status] }),
        CALENDAR_ROUTE
      );
    },
    [goToCalendarWithDashboardFilters, getInfoToCache, CALENDAR_ROUTE]
  );

  return { goWithFilter };
}
